export interface StreamRequest {
  model: string;
  sessionId: string;
  clientRequestId: string;
  requestPurpose?: "story" | "continue" | "cut_recovery" | "summary" | "character_creation" | "card_assistance";
  maxOutputTokens?: number;
  responseLengthPreset?: string;
  reasoningEffort?: string;
  verbosity?: string;
  temperature?: number;
  messages: { role: "user" | "assistant"; content: string }[];
  instructionBlocks?: { id: string; stability: string; content: string; revision?: string }[];
  promptManifest?: Record<string, unknown>;
  agent?: { role: "main_gm" | "creator_copilot" | "player_ooc" | "helper"; enabledToolIds: string[]; phase?: "planning" | "narration" | "postprocessing" | "resume" };
}

export interface StreamEvent {
  type: string;
  delta?: string;
  status?: string;
  reason?: string;
  [key: string]: unknown;
}

export interface StreamResult {
  terminal: StreamEvent | null;
  usage: StreamEvent | null;
}

import { getFreshAccessToken } from "./supabase";

export async function streamNarration(
  endpoint: string,
  token: string,
  request: StreamRequest,
  onEvent: (event: StreamEvent) => void,
  signal?: AbortSignal,
): Promise<StreamResult> {
  let activeToken = token;
  let response = await fetch(`${endpoint.replace(/\/$/, "")}/v1/chat/stream`, {
    method: "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${activeToken}` },
    body: JSON.stringify({ ...request, stream: true, client_channel: "web_pwa" }),
    signal: signal ?? null,
  });

  // If unauthorized (e.g. JWT expired during long multi-step run), try refreshing session once
  if (response.status === 401) {
    const refreshedToken = await getFreshAccessToken(true).catch(() => null);
    if (refreshedToken && refreshedToken !== activeToken) {
      activeToken = refreshedToken;
      response = await fetch(`${endpoint.replace(/\/$/, "")}/v1/chat/stream`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${activeToken}` },
        body: JSON.stringify({ ...request, stream: true, client_channel: "web_pwa" }),
        signal: signal ?? null,
      });
    }
  }

  if (!response.ok || !response.body) {
    const body = await response.text().catch(() => "");
    let message = (() => { try { return String(JSON.parse(body).error || ""); } catch { return body; } })();
    if (response.status === 401 || message === "Unauthorized") {
      message = "เซสชันการเข้าสู่ระบบหมดอายุ กรุณารีเฟรชหน้าเว็บหรือเข้าสู่ระบบใหม่อีกครั้ง";
    }
    throw new Error(message || `Gateway returned ${response.status}`);
  }


  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let terminal: StreamEvent | null = null;
  let usage: StreamEvent | null = null;
  const emit = (event: StreamEvent) => {
    if (event.type === "terminal") terminal = event;
    if (event.type === "usage") usage = event;
    onEvent(event);
  };
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split("\n\n");
    buffer = frames.pop() ?? "";
    for (const frame of frames) {
      const data = frame.split("\n").filter((line) => line.startsWith("data:")).map((line) => line.slice(5).trim()).join("\n");
      if (!data || data === "[DONE]") continue;
      emit(JSON.parse(data) as StreamEvent);
    }
  }
  const tail = buffer.split("\n").filter((line) => line.startsWith("data:")).map((line) => line.slice(5).trim()).join("\n");
  if (tail && tail !== "[DONE]") emit(JSON.parse(tail) as StreamEvent);
  return { terminal, usage };
}
